import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Table, Button, Card, CardBody, CardHeader,
  Form, FormGroup, Label, Input, Alert, Modal, ModalHeader, ModalBody, ModalFooter,
  Badge, Spinner, Progress
} from 'reactstrap';
import { FaSearch, FaSync, FaPlay, FaStop, FaDownload, FaFilter, FaCog, FaChartLine } from 'react-icons/fa';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Released = () => {
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState({});
  const [eppAccounts, setEppAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Enrichment
  const [enrichmentSources, setEnrichmentSources] = useState([]);
  const [availableDataSources, setAvailableDataSources] = useState([]);
  const [selectedDataSources, setSelectedDataSources] = useState(() => {
    const saved = localStorage.getItem('selectedDataSourcesReleased');
    return saved ? JSON.parse(saved) : [];
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ pages: 1, total: 0 });

  // Filters
  const [filters, setFilters] = useState({
    domain: '',
    tld: '',
    release_date_from: '',
    release_date_to: '',
    char_type: ''
  });

  // Sorting
  const [sortBy, setSortBy] = useState('domain');
  const [sortOrder, setSortOrder] = useState('asc');

  // Modals
  const [populateModal, setPopulateModal] = useState(false);
  const [checkModal, setCheckModal] = useState(false);
  const [progressModal, setProgressModal] = useState(false);

  // Domain checking
  const [populateForm, setPopulateForm] = useState({ target_date: '' });
  const [checkForm, setCheckForm] = useState({ release_date: '', epp_account_id: '' });
  const [checkSession, setCheckSession] = useState(null);
  const [checkProgress, setCheckProgress] = useState(null);
  const [activeChecks, setActiveChecks] = useState(() => {
    // Load active checks from localStorage on component mount
    const saved = localStorage.getItem('activeReleasedChecks');
    return saved ? JSON.parse(saved) : [];
  }); // Store running checks
  const [checksProgress, setChecksProgress] = useState(() => {
    // Load progress data from localStorage on component mount
    const savedProgress = localStorage.getItem('activeReleasedChecksProgress');
    return savedProgress ? JSON.parse(savedProgress) : {};
  }); // Progress data for each check

  useEffect(() => {
    loadReleasedDomains();
    loadStats();
    loadEppAccounts();
    loadAvailableDataSources();
  }, [currentPage, filters, sortBy, sortOrder]);

  const loadAvailableDataSources = async () => {
    try {
      const response = await api.get('/data-sources/');
      setAvailableDataSources(response.data.sources || []);
    } catch (err) {
      console.error('Failed to load data sources:', err);
    }
  };

  useEffect(() => {
    let interval;
    if (checkSession && progressModal) {
      // Poll for progress updates every 2 seconds
      interval = setInterval(loadProgress, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [checkSession, progressModal]);

  const loadReleasedDomains = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        per_page: 50,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };

      const response = await api.get('/released-domains/', { params });
      setDomains(response.data.domains);
      setPagination(response.data.pagination);
      setEnrichmentSources(response.data.enrichment_sources || []);
    } catch (err) {
      setError('Failed to load released domains');
      console.error('Load domains error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/released-domains/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Load stats error:', err);
    }
  };

  const loadEppAccounts = async () => {
    try {
      if (user?.role === 'admin' || user?.role === 'manager') {
        const response = await api.get('/released-domains/epp-accounts');
        setEppAccounts(response.data.accounts);
      }
    } catch (err) {
      console.error('Load EPP accounts error:', err);
    }
  };

  const loadProgress = async () => {
    if (!checkSession) return;

    try {
      const response = await api.get(`/released-domains/check/${checkSession}/progress`);
      setCheckProgress(response.data.progress);

      // Close modal if completed or failed
      if (response.data.progress.status === 'completed' || response.data.progress.status === 'failed') {
        setTimeout(() => {
          setProgressModal(false);
          setCheckSession(null);
          setCheckProgress(null);
          loadReleasedDomains(); // Refresh data
          loadStats();
        }, 3000);
      }
    } catch (err) {
      console.error('Load progress error:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePopulateDomains = async () => {
    try {
      const response = await api.post('/released-domains/populate', populateForm);
      setSuccess(`Successfully populated ${response.data.inserted} released domains`);
      setPopulateModal(false);
      setPopulateForm({ target_date: '' });
      loadReleasedDomains();
      loadStats();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to populate released domains';
      setError(errorMessage);
      console.error('Populate error:', err);
    }
  };

  const handleStartCheck = async () => {
    try {
      const response = await api.post('/released-domains/check', checkForm);
      const sessionId = response.data.session_id;
      setCheckSession(sessionId);

      // Add to active checks list
      const newCheck = {
        sessionId: sessionId,
        releaseDate: checkForm.release_date,
        startTime: new Date(),
        type: 'manual'
      };
      setActiveChecks(prev => [...prev, newCheck]);

      setSuccess('Domain check started successfully');
      setCheckModal(false);
      setProgressModal(true);
      setCheckForm({ release_date: '', epp_account_id: '' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to start domain check';
      setError(errorMessage);
      console.error('Start check error:', err);
    }
  };

  const handleCancelCheck = async () => {
    if (!checkSession) return;

    try {
      await api.post(`/released-domains/check/${checkSession}/cancel`);
      setSuccess('Domain check cancelled');
      setProgressModal(false);
      setCheckSession(null);
      setCheckProgress(null);
    } catch (err) {
      setError('Failed to cancel domain check');
      console.error('Cancel check error:', err);
    }
  };

  const handleQuickCheck = async () => {
    try {
      const response = await api.post('/released-domains/quick-check');
      const sessionId = response.data.session_id;
      setCheckSession(sessionId);

      // Add to active checks list
      const newCheck = {
        sessionId: sessionId,
        releaseDate: 'yesterday',
        startTime: new Date(),
        type: 'quick'
      };
      setActiveChecks(prev => [...prev, newCheck]);

      setSuccess(response.data.message);
      setProgressModal(true);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to start quick check';
      setError(errorMsg);
      console.error('Quick check error:', err);
    }
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : '-';
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    // Trigger data reload after sort change
    setTimeout(() => loadReleasedDomains(), 0);
  };

  // Function to open progress modal for specific session
  const openProgressModal = (sessionId) => {
    setCheckSession(sessionId);
    setProgressModal(true);
  };

  // Function to remove completed checks from active list
  const removeCompletedCheck = (sessionId) => {
    setActiveChecks(prev => {
      const updated = prev.filter(check => check.sessionId !== sessionId);
      localStorage.setItem('activeReleasedChecks', JSON.stringify(updated));
      return updated;
    });

    // Also remove progress data
    setChecksProgress(prev => {
      const updated = { ...prev };
      delete updated[sessionId];
      localStorage.setItem('activeReleasedChecksProgress', JSON.stringify(updated));
      return updated;
    });
  };

  // Update localStorage when activeChecks changes
  useEffect(() => {
    localStorage.setItem('activeReleasedChecks', JSON.stringify(activeChecks));
  }, [activeChecks]);

  // Update localStorage when checksProgress changes
  useEffect(() => {
    localStorage.setItem('activeReleasedChecksProgress', JSON.stringify(checksProgress));
  }, [checksProgress]);

  // Check progress for all active checks periodically
  useEffect(() => {
    let interval;

    if (activeChecks.length > 0) {
      const checkAllProgress = async () => {
        const newProgress = {};

        for (const check of activeChecks) {
          try {
            const response = await api.get(`/released-domains/progress/${check.sessionId}`);
            const progress = response.data;

            if (progress) {
              newProgress[check.sessionId] = progress;

              if (progress.status === 'completed' || progress.status === 'failed') {
                // Schedule removal of completed checks
                setTimeout(() => {
                  removeCompletedCheck(check.sessionId);
                }, 300000); // 5 minutes
              }
            }
          } catch (err) {
            // If session not found or error, remove from active checks
            if (err.response?.status === 404) {
              removeCompletedCheck(check.sessionId);
            }
          }
        }

        setChecksProgress(newProgress);
      };

      // Check initially
      checkAllProgress();

      // Set up periodic checking
      interval = setInterval(checkAllProgress, 2000); // Every 2 seconds
    } else {
      setChecksProgress({});
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeChecks.length]); // Depend on activeChecks.length to restart when checks are added/removed

  // Cleanup orphaned progress data on mount
  useEffect(() => {
    const activeSessionIds = new Set(activeChecks.map(check => check.sessionId));
    setChecksProgress(prev => {
      const cleaned = {};
      Object.keys(prev).forEach(sessionId => {
        if (activeSessionIds.has(sessionId)) {
          cleaned[sessionId] = prev[sessionId];
        }
      });
      localStorage.setItem('activeReleasedChecksProgress', JSON.stringify(cleaned));
      return cleaned;
    });
  }, []); // Run once on mount

  // Update progress tracking to mark completed checks
  useEffect(() => {
    if (checkProgress && (checkProgress.status === 'completed' || checkProgress.status === 'failed')) {
      const currentCheck = activeChecks.find(check => check.sessionId === checkSession);
      if (currentCheck) {
        // Keep completed check visible for a few minutes before removing
        setTimeout(() => {
          removeCompletedCheck(checkSession);
        }, 300000); // 5 minutes
      }
    }
  }, [checkProgress, checkSession, activeChecks]);

  const formatDateOnly = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : '-';
  };

  const getAvailabilityBadge = (isAvailable, lastChecked) => {
    if (lastChecked === '1970-01-01T00:00:00' || !lastChecked) {
      return <Badge className="bg-dark text-warning border border-warning">Not Checked</Badge>;
    }

    if (isAvailable) {
      return <Badge className="bg-dark text-success border border-success">Available</Badge>;
    } else {
      return <Badge className="bg-dark text-danger border border-danger">Not Available</Badge>;
    }
  };

  return (
    <Container fluid>
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-heading">
                <FaDownload className="page-heading-icon" />
                Released Domains
              </h2>
              <small className="text-muted">Track availability of expired domains</small>
            </div>
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div className="d-flex align-items-center">
                {/* Active Checks Progress Indicators */}
                {activeChecks.map((check, index) => {
                  const progress = checksProgress[check.sessionId];
                  const progressPercent = progress?.progress_percent || 0;
                  const isCompleted = progress?.status === 'completed' || progress?.status === 'failed';
                  const isRunning = progress?.status === 'running';

                  return (
                    <div
                      key={check.sessionId}
                      className={`me-3 p-2 border rounded cursor-pointer ${
                        isCompleted ? 'bg-success text-white' : isRunning ? 'bg-info text-white' : 'bg-light'
                      }`}
                      onClick={() => openProgressModal(check.sessionId)}
                      style={{ cursor: 'pointer', minWidth: '140px' }}
                      title={`Click to view progress for ${check.type} check (${check.releaseDate})\n${progress ?
                        `${progress.checked_domains}/${progress.total_domains} domains (${progress.domains_per_second}/sec)` :
                        'Loading...'}`}
                    >
                      <div className="d-flex align-items-center">
                        <FaChartLine className={`me-2 ${isCompleted ? 'text-light' : 'text-primary'}`} />
                        <div className="flex-grow-1">
                          <small className={`d-block ${isCompleted ? 'text-light' : 'text-muted'}`}>
                            {check.type === 'quick' ? 'Quick Check' : check.releaseDate}
                          </small>
                          <div className="progress" style={{ height: '6px' }}>
                            <div
                              className={`progress-bar ${
                                isCompleted ? 'bg-light' : isRunning ? 'progress-bar-striped progress-bar-animated bg-warning' : ''
                              }`}
                              role="progressbar"
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                          {progress && (
                            <small className={`d-block ${isCompleted ? 'text-light' : 'text-muted'}`}>
                              {progress.checked_domains}/{progress.total_domains} ({progressPercent}%)
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Control Buttons */}
                <div className="btn-group">
                  <Button color="secondary" onClick={() => setPopulateModal(true)}>
                    <FaSync className="me-1" />
                    Populate
                  </Button>
                  <Button color="primary" onClick={() => setCheckModal(true)}>
                    <FaPlay className="me-1" />
                    Check Domains
                  </Button>
                  <Button color="success" onClick={handleQuickCheck}>
                    <FaSync className="me-1" />
                    Quick Check Yesterday
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {error && <Alert color="danger" dismissible onDismiss={() => setError('')}>{error}</Alert>}
      {success && <Alert color="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}

      {/* Statistics */}
      <Row className="mb-4">
        <Col md="3">
          <Card>
            <CardBody>
              <h6 className="text-muted">Total Domains</h6>
              <h3>{stats.total_domains?.toLocaleString() || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card>
            <CardBody>
              <h6 className="text-muted">Available</h6>
              <h3 className="text-success">{stats.available_domains?.toLocaleString() || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card>
            <CardBody>
              <h6 className="text-muted">Not Available</h6>
              <h3 className="text-danger">{stats.unavailable_domains?.toLocaleString() || 0}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card>
            <CardBody>
              <h6 className="text-muted">Never Checked</h6>
              <h3 className="text-warning">{stats.never_checked?.toLocaleString() || 0}</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="bg-dark border-secondary mb-4">
        <CardHeader>
          <FaFilter className="me-2" />
          Filters
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="2">
              <FormGroup>
                <Label for="domain">Domain</Label>
                <Input
                  type="text"
                  id="domain"
                  value={filters.domain}
                  onChange={(e) => handleFilterChange('domain', e.target.value)}
                  placeholder="Search domain..."
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="tld">TLD</Label>
                <Input
                  type="text"
                  id="tld"
                  value={filters.tld}
                  onChange={(e) => handleFilterChange('tld', e.target.value)}
                  placeholder="com, net, org..."
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="release_date_from">Release Date From</Label>
                <Input
                  type="date"
                  id="release_date_from"
                  value={filters.release_date_from}
                  onChange={(e) => handleFilterChange('release_date_from', e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="release_date_to">Release Date To</Label>
                <Input
                  type="date"
                  id="release_date_to"
                  value={filters.release_date_to}
                  onChange={(e) => handleFilterChange('release_date_to', e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="char_type">Character Type</Label>
                <Input
                  type="select"
                  id="char_type"
                  value={filters.char_type}
                  onChange={(e) => handleFilterChange('char_type', e.target.value)}
                >
                  <option value="">All Characters</option>
                  <option value="alpha">Alpha Only (a-z)</option>
                  <option value="alphanum">Alphanumeric (a-z,0-9)</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>&nbsp;</Label>
                <div>
                  <Button color="outline-secondary" onClick={() => {
                    setFilters({
                      domain: '',
                      tld: '',
                      release_date_from: '',
                      release_date_to: '',
                      char_type: ''
                    });
                    setCurrentPage(1);
                  }}>
                    Clear Filters
                  </Button>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Data Sources Selector */}
      {availableDataSources.length > 0 && (
        <Card className="bg-dark border-secondary mb-4">
          <CardBody>
            <Label className="text-white">Data Sources (select to show enrichment data)</Label>
            <div className="d-flex flex-wrap gap-2">
              {availableDataSources.map(source => (
                <div key={source.id} className="form-check form-check-inline">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`ds-released-${source.id}`}
                    checked={selectedDataSources.includes(source.id)}
                    onChange={(e) => {
                      const newSelection = e.target.checked
                        ? [...selectedDataSources, source.id]
                        : selectedDataSources.filter(id => id !== source.id);
                      setSelectedDataSources(newSelection);
                      localStorage.setItem('selectedDataSourcesReleased', JSON.stringify(newSelection));
                    }}
                  />
                  <label className="form-check-label text-white" htmlFor={`ds-released-${source.id}`}>
                    {source.name}
                  </label>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Released Domains Table */}
      <Card className="bg-dark border-secondary">
        <CardHeader>
          Released Domains
          <small className="text-muted ms-2">({pagination.total} total)</small>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <div>Loading released domains...</div>
            </div>
          ) : domains.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table dark hover striped>
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('domain')}>
                        Domain {sortBy === 'domain' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>TLD</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('length')}>
                        Length {sortBy === 'length' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('release_date')}>
                        Release Date {sortBy === 'release_date' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('last_checked')}>
                        Last Checked {sortBy === 'last_checked' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('is_available')}>
                        Availability {sortBy === 'is_available' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      {selectedDataSources.length > 0 && enrichmentSources.map(source => {
                        if (!selectedDataSources.includes(source.id)) return null;
                        const columns = [];
                        columns.push(
                          <th key={`${source.id}_main`} style={{ cursor: 'pointer' }} onClick={() => handleSort(`ds${source.id}_main`)}>
                            {source.response_mapping?.main_value_label || source.name} {sortBy === `ds${source.id}_main` && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                        );
                        if (source.response_mapping?.additional_values) {
                          source.response_mapping.additional_values.forEach((val) => {
                            const colKey = `ds${source.id}_${val.field.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                            columns.push(
                              <th key={colKey} style={{ cursor: 'pointer' }} onClick={() => handleSort(colKey)}>
                                {val.label} {sortBy === colKey && (sortOrder === 'asc' ? '↑' : '↓')}
                              </th>
                            );
                          });
                        }
                        return columns;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((domain) => {
                      // Calculate domain length without TLD
                      const domainWithoutTld = domain.domain.replace(`.${domain.tld}`, '');

                      return (
                        <tr key={domain.domain}>
                          <td><strong>{domain.domain}</strong></td>
                          <td>
                            <Badge className="bg-dark text-white border border-secondary">.{domain.tld}</Badge>
                          </td>
                          <td>
                            <Badge className="bg-dark text-info border border-info">{domainWithoutTld.length}</Badge>
                          </td>
                          <td>{formatDateOnly(domain.release_date)}</td>
                          <td>{formatDate(domain.last_checked)}</td>
                          <td>{getAvailabilityBadge(domain.is_available, domain.last_checked)}</td>
                          {selectedDataSources.length > 0 && enrichmentSources.map(source => {
                            if (!selectedDataSources.includes(source.id)) return null;
                            const cells = [];
                            cells.push(
                              <td key={`${source.id}_main`}>
                                <Badge className="bg-dark text-info border border-info">
                                  {domain.enrichment?.[`ds${source.id}_main`] || '-'}
                                </Badge>
                              </td>
                            );
                            if (source.response_mapping?.additional_values) {
                              source.response_mapping.additional_values.forEach((val) => {
                                const colKey = `ds${source.id}_${val.field.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                                cells.push(
                                  <td key={colKey}>
                                    <small className="text-muted">
                                      {domain.enrichment?.[colKey] || '-'}
                                    </small>
                                  </td>
                                );
                              });
                            }
                            return cells;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <div className="btn-group">
                    <Button
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button disabled>
                      Page {currentPage} of {pagination.pages}
                    </Button>
                    <Button
                      disabled={currentPage >= pagination.pages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <h5 className="text-muted">No released domains found</h5>
              <p className="text-muted">Try adjusting your filters or populate domains first.</p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Populate Modal */}
      <Modal isOpen={populateModal} toggle={() => setPopulateModal(false)}>
        <ModalHeader toggle={() => setPopulateModal(false)}>
          Populate Released Domains
        </ModalHeader>
        <ModalBody>
          <p>Import released domains from expired domain records for specific date.</p>
          <FormGroup>
            <Label for="target_date">Target Date</Label>
            <Input
              type="date"
              id="target_date"
              value={populateForm.target_date}
              onChange={(e) => setPopulateForm({...populateForm, target_date: e.target.value})}
              required
            />
            <small className="text-muted">Import domains that expired on this date</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPopulateModal(false)}>Cancel</Button>
          <Button color="primary" onClick={handlePopulateDomains} disabled={!populateForm.target_date}>
            Populate
          </Button>
        </ModalFooter>
      </Modal>

      {/* Check Domains Modal */}
      <Modal isOpen={checkModal} toggle={() => setCheckModal(false)}>
        <ModalHeader toggle={() => setCheckModal(false)}>
          Check Domain Availability
        </ModalHeader>
        <ModalBody>
          <p>Start EPP check for domains released on specific date.</p>
          <FormGroup>
            <Label for="release_date">Release Date</Label>
            <Input
              type="date"
              id="release_date"
              value={checkForm.release_date}
              onChange={(e) => setCheckForm({...checkForm, release_date: e.target.value})}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label for="epp_account">EPP Account</Label>
            <Input
              type="select"
              id="epp_account"
              value={checkForm.epp_account_id}
              onChange={(e) => setCheckForm({...checkForm, epp_account_id: e.target.value})}
              required
            >
              <option value="">Select EPP Account</option>
              {eppAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.registrar_category})
                  {!account.is_connected && ' - Not Connected'}
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCheckModal(false)}>Cancel</Button>
          <Button color="primary" onClick={handleStartCheck}
                  disabled={!checkForm.release_date || !checkForm.epp_account_id}>
            Start Check
          </Button>
        </ModalFooter>
      </Modal>

      {/* Progress Modal */}
      <Modal isOpen={progressModal} toggle={() => setProgressModal(false)}>
        <ModalHeader toggle={() => setProgressModal(false)}>
          Domain Check Progress
        </ModalHeader>
        <ModalBody>
          {checkProgress ? (
            <div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Progress</span>
                  <span>{checkProgress.progress_percent}%</span>
                </div>
                <Progress
                  value={checkProgress.progress_percent}
                  color={checkProgress.status === 'failed' ? 'danger' : 'primary'}
                />
              </div>

              <div className="mb-3">
                <strong>Status:</strong> {checkProgress.status}
                {checkProgress.error_message && (
                  <div className="text-danger mt-1">{checkProgress.error_message}</div>
                )}
              </div>

              <Row>
                <Col md="6">
                  <div><strong>Total Domains:</strong> {checkProgress.total_domains}</div>
                  <div><strong>Checked:</strong> {checkProgress.checked_domains}</div>
                  <div><strong>Available:</strong> {checkProgress.available_domains}</div>
                  <div><strong>Failed:</strong> {checkProgress.failed_domains}</div>
                </Col>
                <Col md="6">
                  <div><strong>Elapsed Time:</strong> {Math.round(checkProgress.elapsed_time)}s</div>
                  <div><strong>Speed:</strong> {checkProgress.domains_per_second} domains/sec</div>
                  <div><strong>ETA:</strong> {Math.round(checkProgress.estimated_remaining)}s</div>
                  <div><strong>Current:</strong> {checkProgress.current_domain}</div>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center">
              <Spinner color="primary" />
              <div>Loading progress...</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {checkProgress?.status === 'running' && (
            <Button color="warning" onClick={handleCancelCheck}>
              <FaStop className="me-1" />
              Cancel Check
            </Button>
          )}
          <Button color="secondary" onClick={() => setProgressModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Released;